import { accountService } from "api/services/account-service";
import { IMFADeviceRequest } from "api/entities/mfa";
import DetailsPanel from "components/shared-ui/details-panel/details-panel";
import React, { useEffect } from "react";
import { connect } from "react-redux";
import { IAppState } from "redux/reducers";
import { formUtils, IFormField, useField } from "utils/form-utils";
import { useApi } from "utils/hooks/use-api";
import styles from "./mfa-device-panel.module.scss";
import { FormPanel } from "components/shared-ui/form/form-panel/form-panel";
import { validators } from "utils/validators";
import MfaDeviceManualSection from "../sections/mfa-device-manual-section/mfa-device-manual-section";

const panelDescription = `Scan the QR code below with your authenticator app, or enter the secret manually, then enter the one-time code generated by your authenticator to verify your device.`;
export interface IMfaSetupForm {
  code?: IFormField<string>;
  qrCodeUrl: IFormField<string>;
  secret: IFormField<string>;
  name: IFormField<string>;
  error?: IFormField<string>;
}

export interface IMfaSetupFormValue {
  code?: string;
  qrUrl: string;
  secret: string;
  name?: string;
}

const mfaSetupFormDefaultValue: IMfaSetupFormValue = {
  qrUrl: "",
  secret: "",
  name: "",
};

const mapStateToProps = (state: IAppState) => ({
  user: state.users.currentUser,
});

export interface ISetupMfaProps extends ReturnType<typeof mapStateToProps> {
  required?: boolean;
  open: boolean;
  onClose: (result?: IMFADeviceRequest) => void;
}

export const MFADevicePanel = connect(
  mapStateToProps,
  null,
)((props: ISetupMfaProps) => {
  const [api] = useApi();

  const formCodeValidators = [validators.validateMFAToken];
  const deviceNameValidators = [validators.validateMFADeviceName];

  const form: IMfaSetupForm = {
    code: useField<string>(mfaSetupFormDefaultValue.code, formCodeValidators),
    qrCodeUrl: useField<string>(mfaSetupFormDefaultValue.qrUrl),
    secret: useField<string>(mfaSetupFormDefaultValue.secret),
    name: useField<string>(mfaSetupFormDefaultValue.name, deviceNameValidators),
    error: useField<string>(""),
  };

  const fetchMfaSetupData = async () => {
    api(accountService.getMFASetupData()).then((response) => {
      formUtils.setFormValue(form, response);
    });
  };

  const verifyDevice = async () => {
    const code = form.code.value;
    const secret = form.secret.value;
    api(accountService.verifyTOTP({ token: code, secret })).then((response) => {
      if (response.valid) {
        const deviceReq: IMFADeviceRequest = {
          name: form.name.value,
          active: false,
          secret: secret,
        };
        props.onClose(deviceReq);
      } else {
        formUtils.setFormValue(form, { error: "Invalid code" });
      }
    });
  };

  useEffect(() => {
    if (props.open) {
      fetchMfaSetupData();
    }
  }, [props.open]);

  useEffect(() => {
    if (form.error.value) {
      formUtils.setFormValue(form, { error: "" });
    }
  }, [form.code.value]);

  return (
    <DetailsPanel
      open={props.open}
      onCloseClick={() => props.onClose()}
      title="MFA Device Setup"
      className={styles.panel}
      description={panelDescription}
    >
      <FormPanel
        className={styles.root}
        okButtonText="Verify"
        okButtonClick={verifyDevice}
        errorMessage={formUtils.getFormError(form) || form.error.value}
        okButtonDisabled={
          !form.code.value || !form.name.value || !!formUtils.getFormError(form)
        }
      >
        <div className={styles.qrCodeSection}>
          <div className={styles.qrCodeImageWrapper}>
            {form.qrCodeUrl.value && (
              <img
                src={form.qrCodeUrl.value}
                alt="QR code for MFA setup"
                className={styles.qrCodeImage}
              />
            )}
          </div>
        </div>
        <div>
          <MfaDeviceManualSection
            nameField={form.name}
            codeField={form.code}
            secretValue={form.secret.value}
            testId="mfa-device-manual"
          />
        </div>
      </FormPanel>
    </DetailsPanel>
  );
});
